import { composeClassNames } from "@faedev/editor-utils";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties } from "react";
import { Card, Row } from "react-bootstrap";
import Favorite from "src/components/Favorite";
import Property from "src/components/Property";
import Rating from "src/components/Rating";
import constants from "src/constants";
import countryNameText from "src/functions/countryNameText";
import getLink from "src/functions/getLink";
import profileName from "src/functions/profileName";
import translate from "src/functions/translate";
import { sInstructorCard } from "src/styles/sInstructorCard.module.scss";
import { sInstructorCardBody } from "src/styles/sInstructorCardBody.module.scss";
import { sInstructorCardFooter } from "src/styles/sInstructorCardFooter.module.scss";
import { sInstructorImg } from "src/styles/sInstructorImg.module.scss";
import { ApiProfileCard } from "src/types/ApiProfileCard";
import ExperienceLevelBadge from "./ExperienceLevelBadge";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  data: ApiProfileCard;
};

function InstructorCard({ style, className, data }: Props) {
  const { locale: lang } = useRouter();

  return data ? (
    <Card
      className={composeClassNames([sInstructorCard, className, "instructor"])}
      style={style}
    >
      <div className={"card-img-outer-wrapper"}>
        <div className={"card-img-wrapper"}>
          {data.thumbnail ? (
            <Image
              src={constants.Paths.imagesUrl + data?.thumbnail?.url}
              className={composeClassNames([sInstructorImg, "card-img"])}
              alt="avatar"
              layout="fill"
            />
          ) : null}
          <ExperienceLevelBadge level={data.experience?.level} />
        </div>
      </div>
      <Card.ImgOverlay className={`instructor-img-overlay`}>
        <Link
          href={getLink(data.locale, `profile`, data.slug, null)}
          passHref
          locale={false}
        >
          <Card.Link />
        </Link>
      </Card.ImgOverlay>
      <Card.Body
        className={composeClassNames([sInstructorCardBody, "instructor-body"])}
      >
        <Card.Title>
          <Link
            href={getLink(data.locale, `profile`, data.slug, null)}
            passHref
            locale={false}
          >
            <Card.Link>
              {data.client
                ? profileName(
                    data.client?.name,
                    data.client?.surname,
                    data.nickname,
                    data.type,
                    `sm`,
                    `0`
                  )
                : ``}
            </Card.Link>
          </Link>
        </Card.Title>
        {constants.Show.navFavorite || constants.Show.navRating ? (
          <Row>
            <Favorite
              setByUser={true}
              size="sm"
              className={`instructor-favorite`}
              colors={{
                default: constants.Theme.secondary,
                active: constants.Theme.primary,
              }}
              value={false}
            />
            <Rating
              value={data.rating}
              className={`instructor-rating`}
              size="sm"
              colors={{
                default: constants.Theme.secondary,
                active: constants.Theme.primary,
              }}
            />
          </Row>
        ) : null}
      </Card.Body>
      <Card.Footer
        className={composeClassNames([
          sInstructorCardFooter,
          "instructor-footer",
        ])}
      >
        {constants.Show.instructorLoyalty ? (
          <>
            <Property
              className={`instructor-property`}
              value={data.loyalty.type}
              size="sm"
              type={`status`}
              showIcon={false}
            />
            {`•`}
          </>
        ) : null}
        <Property
          className={`instructor-property`}
          size="sm"
          type={`country`}
          showIcon={false}
          showLabel={false}
          value={countryNameText(
            data?.locale,
            lang,
            data?.address?.country,
            data?.address?.city
          )}
        />
        {data.followers > 0 ? (
          <Property
            className={`instructor-property`}
            value={String(data.followers) + ` ` + translate(`followers`, lang)}
            size="sm"
            type={`followers`}
            showIcon={false}
            showLabel={false}
          />
        ) : null}
      </Card.Footer>
    </Card>
  ) : null;
}

export default InstructorCard;
